import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { InsertDriveFile as FileIcon } from '@mui/icons-material';
import {
  isImageDocument,
  isPdfDocument,
  renderPdfFirstPageThumbnail,
} from '../../utils/patientDocumentPreview';

export interface PatientDocumentThumbSource {
  file_url: string;
  file_type?: string | null;
  file_name?: string | null;
  metadata?: { thumbnail_url?: string } | null;
}

interface PatientDocumentThumbnailProps {
  doc: PatientDocumentThumbSource;
  size?: number;
  accentColor?: string;
}

export default function PatientDocumentThumbnail({
  doc,
  size = 44,
  accentColor = '#0891b2',
}: PatientDocumentThumbnailProps) {
  const storedThumb = doc.metadata?.thumbnail_url;
  const imageUrl = isImageDocument(doc.file_type, doc.file_name) ? doc.file_url : null;
  const [thumb, setThumb] = useState<string | null>(storedThumb || imageUrl);
  const [loading, setLoading] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (thumb) return;
    if (!isPdfDocument(doc.file_type, doc.file_name)) return;

    const node = rootRef.current;
    if (!node) return;

    let cancelled = false;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        observer.disconnect();
        setLoading(true);
        void renderPdfFirstPageThumbnail(doc.file_url, size * 2)
          .then((url) => {
            if (!cancelled && url) setThumb(url);
          })
          .finally(() => {
            if (!cancelled) setLoading(false);
          });
      },
      { rootMargin: '100px' },
    );

    observer.observe(node);
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [doc.file_url, doc.file_type, doc.file_name, size, thumb]);

  return (
    <Box
      ref={rootRef}
      sx={{
        width: size,
        height: size,
        borderRadius: 1.5,
        flexShrink: 0,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: thumb ? '#f1f5f9' : `${accentColor}15`,
        border: thumb ? '1px solid #e2e8f0' : 'none',
        position: 'relative',
      }}
    >
      {loading && (
        <CircularProgress
          size={Math.max(14, Math.round(size * 0.36))}
          sx={{ color: accentColor, position: 'absolute' }}
        />
      )}
      {thumb ? (
        <Box
          component="img"
          src={thumb}
          alt=""
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
            opacity: loading ? 0.35 : 1,
          }}
        />
      ) : (
        !loading && <FileIcon sx={{ fontSize: Math.round(size * 0.5), color: accentColor }} />
      )}
    </Box>
  );
}
